import { useRef, useEffect, useState } from 'react';

export interface ConsultingProfileItem {
  icon: string;
  color: string;
  title: string;
  desc: string;
}

export interface ConsultingWhoForData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  profiles: ConsultingProfileItem[];
}

interface ConsultingWhoForProps {
  data: ConsultingWhoForData;
  isEditing?: boolean;
  onChange?: (data: ConsultingWhoForData) => void;
}

const ConsultingWhoFor: React.FC<ConsultingWhoForProps> = ({
  data,
  isEditing = false,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleProfileChange = (index: number, field: 'title' | 'desc' | 'color', val: string) => {
    if (!onChange) return;
    const updated = data.profiles.map((p, i) => (i === index ? { ...p, [field]: val } : p));
    onChange({ ...data, profiles: updated });
  };

  const handleAddProfile = () => {
    if (!onChange) return;
    const newProfile: ConsultingProfileItem = {
      icon: 'ri-user-star-line',
      color: '#8c52ff',
      title: 'Nuevo Perfil de Cliente',
      desc: 'Descripción clara de a quién va dirigido este servicio y qué problemas resuelve.',
    };
    onChange({ ...data, profiles: [...data.profiles, newProfile] });
  };

  const handleDeleteProfile = (index: number) => {
    if (!onChange) return;
    onChange({ ...data, profiles: data.profiles.filter((_, i) => i !== index) });
  };

  return (
    <section className="relative py-24 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(140,82,255,0.08)_0%,transparent_60%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#FF2DAA]/10 border border-[#FF2DAA]/30 rounded-full px-4 py-1.5 text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-5">
            <i className="ri-user-heart-line" />
            {isEditing ? (
              <input
                type="text"
                value={data.badge}
                onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
                className="bg-black/40 border border-pink-500/40 rounded px-2 text-[#FF2DAA] text-xs font-bold uppercase tracking-widest focus:outline-none"
              />
            ) : (
              <span>{data.badge}</span>
            )}
          </div>

          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3 mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={data.titlePrefix}
                  onChange={(e) => onChange && onChange({ ...data, titlePrefix: e.target.value })}
                  className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white font-black text-2xl text-right focus:outline-none"
                />
                <input
                  type="text"
                  value={data.titleHighlight}
                  onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                  className="flex-1 bg-black/40 border border-pink-500/40 rounded px-3 py-2 text-pink-400 font-black text-2xl text-left focus:outline-none"
                />
              </div>
              <textarea
                value={data.subtitle}
                onChange={(e) => onChange && onChange({ ...data, subtitle: e.target.value })}
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-1.5 text-white/60 text-sm text-center focus:outline-none"
              />
            </div>
          ) : (
            <>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                {data.titlePrefix}{' '}
                <span className="bg-gradient-to-r from-[#FF2DAA] to-[#8c52ff] bg-clip-text text-transparent">
                  {data.titleHighlight}
                </span>
              </h2>
              <p className="text-white/50 text-base max-w-xl mx-auto">
                {data.subtitle}
              </p>
            </>
          )}

          {/* Add Profile button in edit mode */}
          {isEditing && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={handleAddProfile}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-user-add-line" />
                Añadir Perfil Objetivo
              </button>
            </div>
          )}
        </div>

        <div
          ref={ref}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {data.profiles.map((p, i) => (
            <div
              key={i}
              className="relative rounded-2xl p-6 border border-white/8 transition-all duration-300 hover:border-white/20 group"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                transitionDelay: `${i * 60}ms`,
              }}
            >
              {isEditing && (
                <button
                  type="button"
                  onClick={() => handleDeleteProfile(i)}
                  className="absolute top-3 right-3 text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/10 cursor-pointer"
                  title="Eliminar perfil"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              )}

              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
                style={{ background: `${p.color}15`, border: `1px solid ${p.color}30` }}
              >
                <i className={`${p.icon} text-xl`} style={{ color: p.color }} />
              </div>

              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={p.title}
                    onChange={(e) => handleProfileChange(i, 'title', e.target.value)}
                    className="w-full bg-black/40 border border-purple-500/40 rounded px-2.5 py-1 text-white font-bold text-sm focus:outline-none"
                  />
                  <textarea
                    value={p.desc}
                    onChange={(e) => handleProfileChange(i, 'desc', e.target.value)}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1 text-white/60 text-xs focus:outline-none focus:border-purple-500"
                  />
                </div>
              ) : (
                <>
                  <h3 className="text-base font-bold text-white mb-2">{p.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{p.desc}</p>
                </>
              )}

              <div
                className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${p.color}60, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConsultingWhoFor;
